import React from 'react';
import styled from 'styled-components';
import { Compass, Heart, CheckCircle2, TrendingUp, Info } from 'lucide-react';
import { adminApi, formatNumber } from '@kardume/services';
import { theme } from '@kardume/theme';
import { Card, CardHeader, CardBody, StatCard } from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from './DataState';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

/**
 * Rodapé explicativo do bloco.
 *
 * Não é enfeite: sem ele o lojista lê "Já foram: 18" como "18 clientes
 * entraram na minha loja", e o número é menor do que isso - a plataforma só
 * enxerga quem validou cupom ou marcou à mão. Um relatório que o leitor
 * interpreta errado é pior do que relatório nenhum, porque ele decide em cima.
 */
const Note = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 1rem 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const Empty = styled.p`
  margin: 0;
  color: ${(p) => p.theme.textMuted};
  font-size: 0.9rem;
  line-height: 1.6;
`;

/** "+3 nos últimos 30 dias" - vazio quando nada se moveu, para não poluir. */
function trend(value: number): string | undefined {
  return value > 0 ? `+${formatNumber(value)} em 30 dias` : undefined;
}

/**
 * Interesse dos clientes na loja (card 214).
 *
 * Carrega separado do painel de resultados, com o próprio `useAsync`: são dois
 * endpoints e duas permissões, e um erro num deles não pode levar o outro
 * embora - o lojista continua vendo os resgates mesmo se este bloco falhar.
 *
 * **Nunca mostra quem salvou.** O contrato do core não devolve nomes, e não é
 * por esquecimento: o lojista tem interesse legítimo no número e nenhum na
 * identidade. Se um dia alguém pedir a lista nominal, isto aqui precisa virar
 * conversa, não um `map` a mais nesta tela.
 */
const VisitIntentInsights: React.FC = () => {
  const { data, loading, error, reload } = useAsync(() => adminApi.getVisitIntentInsights(), []);

  const semDado = !!data && data.total.total === 0;

  return (
    <Card>
      <CardHeader>
        <h3>Interesse dos clientes</h3>
      </CardHeader>
      <CardBody>
        <DataState loading={loading} error={error} onRetry={reload}>
          {data && (
            <>
              {semDado ? (
                <Empty>
                  Ninguém salvou a sua loja ainda. Assim que alguém marcar &quot;quero conhecer&quot;,
                  &quot;favorito&quot; ou &quot;já fui&quot; no aplicativo, os números aparecem aqui.
                </Empty>
              ) : (
                <Grid>
                  <StatCard
                    label="Querem conhecer"
                    value={formatNumber(data.total.wantToGo)}
                    icon={<Compass size={18} />}
                    iconColor={theme.info}
                    delta={trend(data.last30Days.wantToGo)}
                    deltaPositive
                  />
                  <StatCard
                    label="Têm como favorita"
                    value={formatNumber(data.total.favorite)}
                    icon={<Heart size={18} />}
                    iconColor={theme.accent}
                    delta={trend(data.last30Days.favorite)}
                    deltaPositive
                  />
                  <StatCard
                    label="Já foram"
                    value={formatNumber(data.total.visited)}
                    icon={<CheckCircle2 size={18} />}
                    iconColor={theme.success}
                    delta={trend(data.last30Days.visited)}
                    deltaPositive
                  />
                  <StatCard
                    label="Viraram visita"
                    value={`${data.conversionRate.toLocaleString('pt-BR', {
                      maximumFractionDigits: 1,
                    })}%`}
                    icon={<TrendingUp size={18} />}
                    iconColor={theme.primary}
                  />
                </Grid>
              )}

              <Note>
                <Info size={15} />
                <span>
                  <strong>&quot;Já foram&quot;</strong> conta quem validou um cupom na sua loja ou
                  marcou a visita no aplicativo - quem entrou sem usar cupom não aparece aqui.{' '}
                  <strong>&quot;Viraram visita&quot;</strong> é a proporção entre quem disse
                  &quot;quero conhecer&quot; e quem de fato foi; quem marcou favorito não entra na
                  conta, porque nunca passou por essa etapa. Os números são sempre agregados: o
                  Kardume não mostra quem salvou a sua loja.
                </span>
              </Note>
            </>
          )}
        </DataState>
      </CardBody>
    </Card>
  );
};

export default VisitIntentInsights;
